/* =========================================================
   Scroll FX — entrances, parallax, scroll-velocity tracking
   Performance: event-driven only, cached offsets, IO entrances
   ========================================================= */
(function () {
  /* ── Device capability detection ── */
  const prefersReduced = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  const isTouch        = ('ontouchstart' in window) || navigator.maxTouchPoints > 0;
  const isLowEnd       = navigator.hardwareConcurrency != null && navigator.hardwareConcurrency <= 4;
  const inEditor       = !!(window.Shopify && window.Shopify.designMode);

  /* On reduced-motion: reveal everything immediately and stop */
  if (prefersReduced) {
    document.querySelectorAll('[data-anim], [data-stagger]').forEach(el => el.classList.add('in'));
    document.querySelector('.nav')?.classList.add('nav-loaded');
    document.querySelector('.hero')?.classList.add('hero-in');
    return;
  }

  /* ── Disable heavy effects on touch / low-end ── */
  const skipParallax = isTouch || isLowEnd;
  const skipCursor   = isTouch;

  if (skipCursor) document.body.classList.add('no-custom-cursor');

  // ---------- 1. Scroll progress bar ----------
  const progress = document.createElement('div');
  progress.className = 'scroll-progress';
  document.body.appendChild(progress);

  // ---------- 2. Nav load-in ----------
  const nav = document.querySelector('.nav');
  if (nav) requestAnimationFrame(() => nav.classList.add('nav-loaded'));

  // ---------- 3. Wrap hero H1 words for stagger ----------
  const heroH1 = document.querySelector('.hero h1');
  if (heroH1) {
    const wrap = (el) => {
      el.childNodes.forEach((node) => {
        if (node.nodeType === Node.TEXT_NODE) {
          const text = node.textContent;
          if (!text.trim()) return;
          const frag = document.createDocumentFragment();
          text.split(/(\s+)/).forEach((part) => {
            if (!part.trim()) {
              frag.appendChild(document.createTextNode(part));
            } else {
              const span = document.createElement('span');
              span.className = 'word';
              span.textContent = part;
              frag.appendChild(span);
            }
          });
          node.replaceWith(frag);
        } else if (node.nodeType === Node.ELEMENT_NODE && !node.classList.contains('word')) {
          wrap(node);
          if (!node.classList.contains('word') && node.tagName !== 'BR') {
            const span = document.createElement('span');
            span.className = 'word';
            node.replaceWith(span);
            span.appendChild(node);
          }
        }
      });
    };
    wrap(heroH1);
  }

  document.querySelectorAll('.hero .section-label, .hero p.lede, .hero-cta').forEach((el, i) => {
    el.setAttribute('data-hero-text', '');
    el.style.setProperty('--d', (0.7 + i * 0.12) + 's');
  });

  requestAnimationFrame(() => {
    document.querySelector('.hero')?.classList.add('hero-in');
  });

  // ---------- 4. Entrance animations via IntersectionObserver ----------
  // IO is zero-cost between frames — vastly cheaper than getBoundingClientRect per scroll tick.
  // Fall back to scroll-based check only inside the Shopify editor where IO can misbehave.
  const animTargets = [...document.querySelectorAll('[data-anim], [data-stagger], [data-section-reveal]')];

  if (!inEditor && 'IntersectionObserver' in window) {
    const io = new IntersectionObserver((entries) => {
      entries.forEach(e => {
        if (e.isIntersecting) {
          e.target.classList.add('in');
          io.unobserve(e.target);
        }
      });
    }, { threshold: 0.06, rootMargin: '0px 0px -6% 0px' });
    animTargets.forEach(el => io.observe(el));
  } else {
    /* editor fallback: check on scroll, remove from list once revealed */
    const remaining = [...animTargets];
    function checkEntrances() {
      const vh = window.innerHeight;
      const trigger = vh * 0.92;
      for (let i = remaining.length - 1; i >= 0; i--) {
        const r = remaining[i].getBoundingClientRect();
        if (r.top < trigger && r.bottom > 0) {
          remaining[i].classList.add('in');
          remaining.splice(i, 1);
        }
      }
    }
    requestAnimationFrame(() => requestAnimationFrame(checkEntrances));
    window.addEventListener('scroll', checkEntrances, { passive: true });
  }

  // ---------- 5. Parallax — cached offsets, event-driven only ----------
  // getBoundingClientRect inside a scroll handler forces layout reflow every frame.
  // Cache element top/height once; recalculate only on resize.
  const parallaxData = skipParallax ? [] :
    [...document.querySelectorAll('[data-parallax]')].map(el => ({
      el,
      speed:   parseFloat(el.dataset.parallax)     || 0.2,
      speedX:  parseFloat(el.dataset.parallaxX)    || 0,
      baseRot: parseFloat(el.dataset.parallaxRot)  || 0,
      top: 0, height: 0   // filled by cacheOffsets()
    }));

  const rotateData = skipParallax ? [] :
    [...document.querySelectorAll('[data-scroll-rotate]')].map(el => ({
      el,
      speed: parseFloat(el.dataset.scrollRotate) || 0.1
    }));

  const skewData = [...document.querySelectorAll('[data-scroll-skew]')].map(el => ({
    el,
    speed: parseFloat(el.dataset.scrollSkew) || 0.04
  }));

  function cacheOffsets() {
    /* called once on load and on resize — NOT on every scroll event */
    parallaxData.forEach(d => {
      const r = d.el.getBoundingClientRect();
      d.top    = r.top + window.scrollY;
      d.height = r.height;
    });
  }
  cacheOffsets();
  window.addEventListener('resize', cacheOffsets, { passive: true });

  // ---------- 6. Scroll update loop — event-driven only ----------
  let lastY    = window.scrollY;
  let velocity = 0;
  let ticking  = false;

  const marquee      = document.querySelector('.marquee');
  const marqueeTrack = document.querySelector('.marquee-track');

  function update() {
    const y  = window.scrollY;
    const vh = window.innerHeight;
    velocity = y - lastY;
    lastY    = y;

    /* Progress bar */
    const docH = document.documentElement.scrollHeight - window.innerHeight;
    progress.style.width = (docH > 0 ? (y / docH) * 100 : 0) + '%';

    /* Nav shrink */
    if (nav) nav.classList.toggle('scrolled', y > 30);

    /* Parallax — cheap arithmetic, no reflow */
    parallaxData.forEach(d => {
      const center = d.top + d.height / 2 - y;
      const offset = (center - vh / 2) / vh;
      const ty  = -offset * d.speed * 100;
      const trX =  offset * d.speedX * 100;
      const rot =  d.baseRot * offset;
      d.el.style.transform = `translate3d(${trX}px,${ty}px,0) rotate(${rot}deg)`;
    });

    /* Continuous scroll-rotate */
    rotateData.forEach(d => {
      d.el.style.transform = `rotate(${y * d.speed}deg)`;
    });

    /* Scroll-skew */
    skewData.forEach(d => {
      const skew = Math.max(-3, Math.min(3, velocity * d.speed));
      d.el.style.transform = `skewY(${skew}deg)`;
    });

    /* Marquee speed reaction */
    if (marquee && marqueeTrack) {
      const abs = Math.abs(velocity);
      marquee.classList.toggle('fast',    abs > 14);
    }

    ticking = false;
  }

  function onScroll() {
    if (!ticking) {
      requestAnimationFrame(update);
      ticking = true;
    }
  }
  window.addEventListener('scroll', onScroll, { passive: true });
  /* Single resize handler already registered above for cacheOffsets */

  /* Run once on load for initial state */
  requestAnimationFrame(update);

  // ---------- 7. Stat counters via IntersectionObserver ----------
  const statEls = [...document.querySelectorAll('[data-count-to]')];
  if (statEls.length && 'IntersectionObserver' in window) {
    const statIO = new IntersectionObserver((entries) => {
      entries.forEach(e => {
        if (!e.isIntersecting) return;
        const el     = e.target;
        const target = parseFloat(el.dataset.countTo);
        if (isNaN(target)) return;
        const suffix = el.dataset.countSuffix || '';
        const dur    = 1200;
        const start  = performance.now();
        const tick   = (now) => {
          const t     = Math.min(1, (now - start) / dur);
          const eased = 1 - Math.pow(1 - t, 3);
          el.textContent = (Number.isInteger(target)
            ? Math.round(target * eased)
            : (target * eased).toFixed(1)) + suffix;
          if (t < 1) requestAnimationFrame(tick);
        };
        requestAnimationFrame(tick);
        statIO.unobserve(el);
      });
    }, { threshold: 0.5 });
    statEls.forEach(el => statIO.observe(el));
  }

  // ---------- 8. Mouse-parallax in hero ----------
  if (!isTouch) {
    const heroStage = document.querySelector('.hero-bowl-stage');
    if (heroStage) {
      const targets = heroStage.querySelectorAll('[data-mouse-parallax]');
      heroStage.addEventListener('mousemove', (e) => {
        const r = heroStage.getBoundingClientRect();
        const x = (e.clientX - r.left)  / r.width  - 0.5;
        const y = (e.clientY - r.top)   / r.height - 0.5;
        targets.forEach(t => {
          const depth = parseFloat(t.dataset.mouseParallax) || 10;
          t.style.setProperty('--mpx', (x * depth) + 'px');
          t.style.setProperty('--mpy', (y * depth) + 'px');
        });
      }, { passive: true });
      heroStage.addEventListener('mouseleave', () => {
        targets.forEach(t => {
          t.style.setProperty('--mpx', '0px');
          t.style.setProperty('--mpy', '0px');
        });
      });
    }
  }

  // ---------- 9. Magnetic CTA buttons (desktop only) ----------
  if (!isTouch) {
    document.querySelectorAll('[data-magnetic]').forEach(btn => {
      btn.addEventListener('mousemove', (e) => {
        const r = btn.getBoundingClientRect();
        const x = e.clientX - r.left - r.width  / 2;
        const y = e.clientY - r.top  - r.height / 2;
        btn.style.transform = `translate(${x * 0.18}px,${y * 0.25}px)`;
      }, { passive: true });
      btn.addEventListener('mouseleave', () => { btn.style.transform = ''; });
    });
  }
})();
